import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Card, requests as creditCardsRequests } from '@ott/shared-credit-cards-logic';

type Props = {
  paymentCardInfo: {
    items: Array<Card>;
    suggests: Array<Card>;
    activeCard: Card;
  };
  excludeCardCountryCodes: Array<{
    code: string;
    message: string;
  }>;
};

const isSameCardsArrays = (initialArray: Card[], comparingArray: Card[]) => {
  return (
    initialArray.length === comparingArray.length &&
    comparingArray.every((card, index) => card.id === initialArray[index].id)
  );
};

export const withCardCountryCodes = (Component: React.FC<Props>) => {
  return (props: PropsWithChildren<Props>) => {
    const {
      paymentCardInfo,
      paymentCardInfo: { items = [] } = {},
      excludeCardCountryCodes,
    } = props;

    const [initialItems, setInitialItems] = useState<Card[]>([]);
    const [filteredItems, setFilteredItems] = useState<Card[]>([]);

    const filterCardsByCountryCode = async () => {
      if (!excludeCardCountryCodes.length) {
        setFilteredItems(items);

        return;
      }

      const filteredByCountryCode: Card[] = [];
      const excludeCodes = excludeCardCountryCodes.map(({ code }) => code.toUpperCase());

      const itemsWithoutVC = items.filter((card) => !card.isVirtualCard);
      const virtualCard = items.find((card) => card.isVirtualCard);

      if (virtualCard) {
        filteredByCountryCode.push(virtualCard);
      }

      const requests = itemsWithoutVC.map(({ bin }) =>
        creditCardsRequests.getCardInfoByBIN({ bin })
      );

      const responses = await Promise.allSettled(requests);

      responses.forEach((response, index) => {
        if (response.status === 'fulfilled') {
          const { countryCode } = response.value;
          if (excludeCodes.length && !excludeCodes.includes(countryCode)) {
            filteredByCountryCode.push(itemsWithoutVC[index]);
          }
        }
      });

      setFilteredItems(filteredByCountryCode);
    };

    useEffect(() => {
      if (!isSameCardsArrays(initialItems, items)) {
        setInitialItems(items);
        filterCardsByCountryCode();
      }
    }, [items, excludeCardCountryCodes]);

    // Что-то внутри падает, если сделать дефолтное значение paymentCardInfo = {}
    // Если пришло falsy, то его и передаем
    const newPaymentCardInfo = paymentCardInfo
      ? {
          ...paymentCardInfo,
          items: filteredItems,
          suggests: filteredItems,
        }
      : paymentCardInfo;

    return <Component {...props} paymentCardInfo={newPaymentCardInfo} />;
  };
};
